import { Component, computed, input, output } from '@angular/core';
import type { LucideIconInput } from '@lucide/angular';
import {
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminModalComponent,
} from '../../admin';
import {
  LeadStatusSummary,
  LeadTransitionOption,
  WorkflowDocumentationStatus,
  WorkflowDocumentationStep,
} from '../../services/leads.service';

/** Execution state for a workflow transition relative to the selected lead. */
export type WorkflowMatrixState =
  | 'completed'
  | 'current'
  | 'available'
  | 'locked'
  | 'skipped'
  | 'cancelled';

/** Column contract kept open for future matrix fields (SLA, automation, etc.). */
export type WorkflowMatrixColumnId =
  | 'step'
  | 'action'
  | 'fromStatus'
  | 'toStatus'
  | 'description'
  | 'state'
  | 'automationRules'
  | 'sla'
  | 'responsibleRole'
  | 'approvalRequired'
  | 'notificationTemplate'
  | 'workflowVersion'
  | 'lastUpdated';

export type WorkflowMatrixColumn = {
  id: WorkflowMatrixColumnId;
  label: string;
  visible: boolean;
};

export type WorkflowMatrixRow = WorkflowDocumentationStep & {
  state: WorkflowMatrixState;
};

const MATRIX_COLUMNS: WorkflowMatrixColumn[] = [
  { id: 'step', label: 'Step #', visible: true },
  { id: 'action', label: 'Workflow Action', visible: true },
  { id: 'fromStatus', label: 'From Status', visible: true },
  { id: 'toStatus', label: 'To Status', visible: true },
  { id: 'description', label: 'Description', visible: true },
  { id: 'state', label: 'State', visible: true },
];

const STATE_META: Record<
  WorkflowMatrixState,
  { label: string; icon: LucideIconInput | null }
> = {
  completed: { label: 'Completed', icon: 'check' },
  current: { label: 'Current', icon: null },
  available: { label: 'Available', icon: null },
  locked: { label: 'Locked', icon: 'lock' },
  skipped: { label: 'Skipped', icon: null },
  cancelled: { label: 'Cancelled', icon: null },
};

@Component({
  selector: 'app-workflow-documentation-modal',
  standalone: true,
  imports: [AdminModalComponent, AdminIconComponent, AdminEmptyStateComponent],
  template: `
    @if (open()) {
      <app-admin-modal
        [title]="title()"
        size="xl"
        titleId="workflow-doc-modal-title"
        (closed)="closed.emit()"
      >
        <div class="workflow-doc">
          <p class="workflow-doc__subtitle">
            {{ subtitle() }}
          </p>

          @if (loading()) {
            <p class="workflow-doc__loading">Loading workflow…</p>
          } @else if (error(); as err) {
            <p class="workflow-doc__error" role="alert">{{ err }}</p>
          } @else if (steps().length === 0) {
            <app-admin-empty-state
              icon="inbox"
              [title]="emptyTitle()"
            />
          } @else {
            <section class="workflow-doc__summary" aria-label="Workflow progress summary">
              <div class="workflow-doc__summary-grid">
                <div class="workflow-doc__stat">
                  <span class="workflow-doc__stat-label">Current Status</span>
                  <strong class="workflow-doc__stat-value">{{ currentStatusName() }}</strong>
                </div>
                <div class="workflow-doc__stat">
                  <span class="workflow-doc__stat-label">Completed Steps</span>
                  <strong class="workflow-doc__stat-value">
                    {{ completedCount() }} / {{ steps().length }}
                  </strong>
                </div>
                <div class="workflow-doc__stat">
                  <span class="workflow-doc__stat-label">Next Available Action</span>
                  <strong class="workflow-doc__stat-value">{{ nextActionLabel() }}</strong>
                </div>
                <div class="workflow-doc__stat">
                  <span class="workflow-doc__stat-label">Progress</span>
                  <strong class="workflow-doc__stat-value">{{ progressPercent() }}%</strong>
                </div>
              </div>
              <div
                class="workflow-doc__progress"
                role="progressbar"
                [attr.aria-valuenow]="progressPercent()"
                aria-valuemin="0"
                aria-valuemax="100"
                [attr.aria-label]="'Workflow progress ' + progressPercent() + '%'"
              >
                <div class="workflow-doc__progress-bar" [style.width.%]="progressPercent()"></div>
              </div>
            </section>

            <section class="workflow-doc__matrix-section" aria-label="Workflow matrix">
              <h3 class="workflow-doc__matrix-title">Workflow Matrix</h3>

              <div class="workflow-doc__table-wrap">
                <table class="workflow-doc__table">
                  <thead>
                    <tr>
                      @for (col of visibleColumns(); track col.id) {
                        <th [attr.data-col]="col.id">{{ col.label }}</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of matrixRows(); track row._id; let odd = $odd) {
                      <tr
                        [class.workflow-doc__row--alt]="odd"
                        [class.workflow-doc__row--completed]="row.state === 'completed'"
                        [class.workflow-doc__row--current]="row.state === 'current'"
                        [class.workflow-doc__row--available]="row.state === 'available'"
                        [class.workflow-doc__row--locked]="row.state === 'locked'"
                        [class.workflow-doc__row--skipped]="row.state === 'skipped'"
                        [class.workflow-doc__row--cancelled]="row.state === 'cancelled'"
                      >
                        @for (col of visibleColumns(); track col.id) {
                          <td [attr.data-col]="col.id">
                            @switch (col.id) {
                              @case ('step') {
                                <span class="workflow-doc__step-num">{{ row.stepNumber }}</span>
                              }
                              @case ('action') {
                                <strong class="workflow-doc__action">{{ row.actionButtonName || row.transitionName }}</strong>
                              }
                              @case ('fromStatus') {
                                {{ row.fromStatus.name }}
                              }
                              @case ('toStatus') {
                                {{ row.toStatus.name }}
                              }
                              @case ('description') {
                                <span class="workflow-doc__desc">
                                  {{ row.description?.trim() || '—' }}
                                </span>
                              }
                              @case ('state') {
                                <span [class]="'workflow-doc__state workflow-doc__state--' + row.state">
                                  @if (stateIcon(row.state); as iconName) {
                                    <app-admin-icon [name]="iconName" [size]="13" />
                                  }
                                  {{ stateLabel(row.state) }}
                                </span>
                              }
                            }
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

                  <footer class="workflow-doc__legend" aria-label="Workflow matrix legend">
                @for (item of legendItems; track item.state) {
                  <span class="workflow-doc__legend-item">
                    <span [class]="'workflow-doc__state workflow-doc__state--' + item.state">
                      @if (item.icon; as iconName) {
                        <app-admin-icon [name]="iconName" [size]="12" />
                      }
                      {{ item.label }}
                    </span>
                  </span>
                }
              </footer>
            </section>
          }
        </div>
      </app-admin-modal>
    }
  `,
  styles: `
    .workflow-doc {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-5);
      min-height: 0;
      flex: 1;
      overflow-y: auto;
      padding-right: var(--cg-space-1);
    }

    .workflow-doc__subtitle {
      margin: 0;
      color: var(--cg-color-text-muted, #64748b);
      font-size: 0.9375rem;
      line-height: 1.5;
    }

    .workflow-doc__loading,
    .workflow-doc__error {
      margin: 0;
      font-size: 0.9375rem;
    }

    .workflow-doc__error {
      color: #b91c1c;
      font-weight: 500;
    }

    .workflow-doc__summary {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-3);
      padding: var(--cg-space-4);
      border: 1px solid var(--cg-color-border);
      border-radius: var(--cg-radius-lg);
      background: color-mix(in srgb, var(--cg-color-bg) 88%, var(--cg-color-card));
    }

    .workflow-doc__summary-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: var(--cg-space-3);
    }

    @media (min-width: 640px) {
      .workflow-doc__summary-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (min-width: 900px) {
      .workflow-doc__summary-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }

    .workflow-doc__stat {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
    }

    .workflow-doc__stat-label {
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--cg-color-text-muted, #64748b);
    }

    .workflow-doc__stat-value {
      font-size: 0.9375rem;
      font-weight: 650;
      color: var(--cg-color-text, #0f172a);
      line-height: 1.35;
      word-break: break-word;
    }

    .workflow-doc__progress {
      height: 0.5rem;
      border-radius: 9999px;
      background: color-mix(in srgb, var(--cg-color-border) 80%, white);
      overflow: hidden;
    }

    .workflow-doc__progress-bar {
      height: 100%;
      border-radius: inherit;
      background: var(--cg-color-primary, #072b61);
      transition: width var(--cg-duration-base, 200ms) var(--cg-ease-standard, ease);
    }

    .workflow-doc__matrix-section {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-3);
      min-width: 0;
    }

    .workflow-doc__matrix-title {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 650;
      color: var(--cg-color-text, #0f172a);
    }

    .workflow-doc__table-wrap {
      width: 100%;
      overflow-x: auto;
      border: 1px solid var(--cg-color-border);
      border-radius: var(--cg-radius-lg);
      background: var(--cg-color-card);
      max-height: min(62vh, 40rem);
      overflow-y: auto;
    }

    .workflow-doc__table {
      width: 100%;
      min-width: 56rem;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 0.875rem;
    }

    .workflow-doc__table thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      padding: 0.7rem 0.85rem;
      text-align: left;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--cg-color-text-muted, #64748b);
      background: color-mix(in srgb, var(--cg-color-bg) 92%, var(--cg-color-card));
      border-bottom: 1px solid var(--cg-color-border);
      white-space: nowrap;
    }

    .workflow-doc__table tbody td {
      padding: 0.75rem 0.85rem;
      vertical-align: top;
      color: var(--cg-color-text, #0f172a);
      border-bottom: 1px solid color-mix(in srgb, var(--cg-color-border) 80%, white);
      line-height: 1.4;
    }

    .workflow-doc__table tbody tr:last-child td {
      border-bottom: none;
    }

    .workflow-doc__table tbody tr {
      transition: background-color var(--cg-duration-fast, 150ms) var(--cg-ease-standard, ease);
    }

    .workflow-doc__row--alt td {
      background: color-mix(in srgb, var(--cg-color-bg) 55%, var(--cg-color-card));
    }

    .workflow-doc__row--completed td {
      background: color-mix(in srgb, var(--cg-color-success, #16a34a) 8%, white);
    }

    .workflow-doc__row--current td {
      background: #eff6ff;
    }

    .workflow-doc__row--available td {
      background: color-mix(in srgb, var(--cg-color-success, #16a34a) 6%, #f0fdf4);
    }

    .workflow-doc__row--locked td,
    .workflow-doc__row--skipped td,
    .workflow-doc__row--cancelled td {
      background: transparent;
    }

    .workflow-doc__row--alt.workflow-doc__row--locked td {
      background: color-mix(in srgb, var(--cg-color-bg) 55%, var(--cg-color-card));
    }

    .workflow-doc__table tbody tr:hover td {
      filter: brightness(0.985);
    }

    .workflow-doc__step-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.5rem;
      font-weight: 700;
      color: var(--cg-color-text-muted, #64748b);
    }

    .workflow-doc__action {
      font-weight: 650;
      color: var(--cg-color-text, #0f172a);
    }

    .workflow-doc__desc {
      display: block;
      max-width: 28rem;
      color: var(--cg-color-text-secondary, #475569);
    }

    .workflow-doc__state {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.2rem 0.55rem;
      border-radius: var(--cg-radius-md, 0.375rem);
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      line-height: 1.2;
      white-space: nowrap;
    }

    .workflow-doc__state--completed {
      background: color-mix(in srgb, var(--cg-color-success, #16a34a) 16%, white);
      color: #15803d;
    }

    .workflow-doc__state--current {
      background: #dbeafe;
      color: #1d4ed8;
    }

    .workflow-doc__state--available {
      background: color-mix(in srgb, var(--cg-color-success, #16a34a) 18%, white);
      color: #166534;
    }

    .workflow-doc__state--locked {
      background: #f1f5f9;
      color: #64748b;
    }

    .workflow-doc__state--skipped {
      background: #ffedd5;
      color: #c2410c;
    }

    .workflow-doc__state--cancelled {
      background: #fee2e2;
      color: #b91c1c;
    }

    .workflow-doc__legend {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cg-space-2) var(--cg-space-3);
      padding-top: var(--cg-space-1);
      font-size: 0.8125rem;
      color: var(--cg-color-text-muted, #64748b);
    }

    .workflow-doc__legend-item {
      display: inline-flex;
      align-items: center;
    }

    td[data-col='step'],
    th[data-col='step'] {
      width: 4.5rem;
    }

    td[data-col='state'],
    th[data-col='state'] {
      width: 7.5rem;
      white-space: nowrap;
    }
  `,
})
export class WorkflowDocumentationModalComponent {
  readonly open = input(false);
  readonly loading = input(false);
  readonly error = input<string | null>(null);
  readonly title = input('Lead Workflow Documentation');
  readonly subtitle = input('Understand the complete workflow process for this lead.');
  readonly emptyTitle = input(
    'No workflow has been configured. Create a workflow from CRM Settings to view the workflow process.',
  );
  readonly statuses = input<WorkflowDocumentationStatus[]>([]);
  readonly steps = input<WorkflowDocumentationStep[]>([]);
  readonly currentStatus = input<LeadStatusSummary | null>(null);
  readonly availableTransitions = input<LeadTransitionOption[]>([]);
  readonly closed = output<void>();

  /** Column registry — toggle `visible` or extend for future matrix fields. */
  readonly columns = MATRIX_COLUMNS;

  readonly legendItems: Array<{
    state: WorkflowMatrixState;
    label: string;
    icon: LucideIconInput | null;
  }> = (
    ['completed', 'current', 'available', 'locked', 'skipped', 'cancelled'] as WorkflowMatrixState[]
  ).map((state) => ({
    state,
    label: STATE_META[state].label,
    icon: STATE_META[state].icon,
  }));

  readonly visibleColumns = computed(() => this.columns.filter((c) => c.visible));

  readonly availableIds = computed(() => new Set(this.availableTransitions().map((t) => t._id)));

  readonly primaryAvailableId = computed(() => this.availableTransitions()[0]?._id ?? null);

  readonly orderedStatuses = computed(() =>
    [...this.statuses()].sort((a, b) => {
      if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
      return a.name.localeCompare(b.name);
    }),
  );

  readonly resolvedCurrentOrder = computed(() => {
    const current = this.currentStatus();
    if (!current?._id) return null;
    const match = this.orderedStatuses().find((s) => s._id === current._id);
    if (match) return match.displayOrder;
    for (const step of this.steps()) {
      if (step.fromStatus._id === current._id) return step.fromStatus.displayOrder;
      if (step.toStatus._id === current._id) return step.toStatus.displayOrder;
    }
    return null;
  });

  readonly isAtWorkflowEnd = computed(
    () => !this.loading() && this.availableTransitions().length === 0 && this.steps().length > 0,
  );

  readonly matrixRows = computed<WorkflowMatrixRow[]>(() => {
    const current = this.currentStatus();
    const availableIds = this.availableIds();
    const primaryId = this.primaryAvailableId();
    const currentOrder = this.resolvedCurrentOrder();
    const currentId = current?._id;
    const atEnd = this.isAtWorkflowEnd();

    return this.steps().map((step) => {
      const availableNow = availableIds.has(step._id);
      const isFromCurrent = !!currentId && step.fromStatus._id === currentId;
      const isPrimary = primaryId != null && step._id === primaryId;
      const isForward = step.toStatus.displayOrder > step.fromStatus.displayOrder;
      const completed =
        !isFromCurrent &&
        currentOrder != null &&
        isForward &&
        step.toStatus.displayOrder <= currentOrder;

      let state: WorkflowMatrixState = 'locked';
      if (isPrimary || (availableNow && isFromCurrent && !primaryId)) {
        state = 'current';
      } else if (availableNow) {
        state = 'available';
      } else if (completed) {
        state = 'completed';
      } else if (currentOrder != null && step.fromStatus.displayOrder < currentOrder && isForward) {
        state = 'skipped';
      } else if (atEnd && !isForward) {
        state = 'cancelled';
      } else if (atEnd && isForward && currentOrder != null && step.fromStatus.displayOrder >= currentOrder) {
        state = 'cancelled';
      } else {
        state = 'locked';
      }

      return { ...step, state };
    });
  });

  readonly completedCount = computed(
    () => this.matrixRows().filter((r) => r.state === 'completed').length,
  );

  readonly progressPercent = computed(() => {
    const path = this.orderedStatuses();
    const currentId = this.currentStatus()?._id;
    if (path.length <= 1) {
      const total = this.steps().length;
      if (total === 0) return 0;
      return Math.round((this.completedCount() / total) * 100);
    }
    if (!currentId) return 0;
    const currentIndex = path.findIndex((s) => s._id === currentId);
    if (currentIndex < 0) {
      const currentOrder = this.resolvedCurrentOrder();
      if (currentOrder == null) return 0;
      let nearest = 0;
      for (let i = 0; i < path.length; i += 1) {
        if (path[i].displayOrder <= currentOrder) nearest = i;
      }
      return Math.round((nearest / (path.length - 1)) * 100);
    }
    return Math.round((currentIndex / (path.length - 1)) * 100);
  });

  readonly currentStatusName = computed(() => {
    const current = this.currentStatus();
    if (!current) return '—';
    const match = this.orderedStatuses().find((s) => s._id === current._id);
    return (match?.name ?? current.name)?.trim() || '—';
  });

  readonly nextActionLabel = computed(() => {
    const available = this.availableTransitions();
    if (available.length > 0) return available[0].transitionName;
    const next = this.matrixRows().find((r) => r.state === 'current' || r.state === 'available');
    return next?.transitionName ?? '—';
  });

  stateLabel(state: WorkflowMatrixState): string {
    return STATE_META[state].label;
  }

  stateIcon(state: WorkflowMatrixState): LucideIconInput | null {
    return STATE_META[state].icon;
  }
}
